import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import { Form } from "react-bootstrap";
import { GripVertical, Plus, X } from "react-bootstrap-icons";
import type { DocumentSubType, Interrogatory } from "@/briefpoint-client";
import { FormModal } from "@/components/Modals/Modal";
import { getDocumentSubTypeRequestName } from "@/lib/utils";

type DraggableInterrogatory = Omit<Partial<Interrogatory>, "id"> & {
  id: string; // This is a number in InterrogatoryResponse
  empty: boolean; // Assigned by getEmptyInterrogatories. Looks like it's always true.
  parentNumber: number; // Assigned by index in getEmptyInterrogatories
  number: number; // Required by InterrogatoryResponse
};

function isDraggableInterrogatory(
  item: Interrogatory | DraggableInterrogatory,
): item is DraggableInterrogatory {
  return "empty" in item && item.empty === true;
}

export default function InterrogatoriesList({
  interrogatoriesList,
  interrogatoryId,
  onAdd,
  onEdit,
  onDelete,
  documentSubType,
  startingNumber,
}: {
  interrogatoriesList: Interrogatory[];
  interrogatoryId: string;
  onAdd: (index: number, text: string) => void;
  onEdit: (id: number, index: number, text?: string) => void;
  onDelete: (interrogatoryId: number) => void;
  documentSubType: DocumentSubType | undefined;
  startingNumber: number;
}) {
  const [list, setList] = useState<Interrogatory[]>([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  const [newItems, setNewItems] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    setList(interrogatoriesList);
  }, [interrogatoriesList]);

  async function handleConfirmDelete() {
    if (confirmRemoveId !== null) {
      const interrogatory = list.find((i) => i.id === confirmRemoveId);
      if (interrogatory) {
        await onDelete(confirmRemoveId);
        const count = newItems.get(interrogatory.number) || 0;
        if (count > 0) {
          setNewItems(new Map(newItems.set(interrogatory.number, 0)));
          setNewItems(new Map(newItems.set(interrogatory.number - 1, count)));
        }
        setConfirmRemoveId(null);
      }
    }
  }

  function getEmptyInterrogatories(index: number): DraggableInterrogatory[] {
    const count = newItems.get(index);
    const emptyInterrogatories: DraggableInterrogatory[] = [];
    if (count) {
      for (let i = 0; i < count; i++) {
        emptyInterrogatories.push({
          id: `${index}-${i}`, // TODO: can this be a number?
          empty: true,
          parentNumber: index,
          number: i,
        });
      }
    }
    return emptyInterrogatories;
  }

  function removeNewItem(number: number, addNew = false) {
    const count = newItems.get(number) || 0;
    if (addNew) {
      if (count > 0) {
        setNewItems(new Map(newItems.set(number, 0)));
        setNewItems(new Map(newItems.set(number + 1, count - 1)));
      }
    } else {
      if (count > 0) {
        setNewItems(new Map(newItems.set(number, count - 1)));
      }
    }
  }

  const reorder = (
    list: (DraggableInterrogatory | Interrogatory)[],
    startIndex: number,
    endIndex: number,
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    const removedId = removed.id;
    const orderedItemList = [];
    let newIndex = 0;
    result.splice(endIndex, 0, removed);
    let index = 0;
    for (const item of result) {
      if (!isDraggableInterrogatory(item)) {
        item.number = index + 1;
        index++;
        if (item.id === removedId) {
          newIndex = item.number;
        }
        orderedItemList.push(item);
      }
    }
    return { orderedList: orderedItemList, newIndex: newIndex };
  };

  const onDragEnd: OnDragEndResponder = (res) => {
    const { draggableId, destination, source } = res;
    if (!destination) return;
    if (destination.index === source.index) return;

    const sourceItem = combinedList[source.index];
    const destinationItem = combinedList[destination.index];
    let parentNumber = isDraggableInterrogatory(destinationItem)
      ? destinationItem.parentNumber
      : destinationItem.number;

    if (isDraggableInterrogatory(sourceItem)) {
      if (
        sourceItem.parentNumber !== parentNumber ||
        !isDraggableInterrogatory(destinationItem)
      ) {
        if (
          destination.index < source.index &&
          !isDraggableInterrogatory(destinationItem)
        ) {
          parentNumber = parentNumber - 1;
        }
        removeNewItem(sourceItem.parentNumber);
        setNewItems(
          new Map(
            newItems.set(parentNumber, (newItems.get(parentNumber) || 0) + 1),
          ),
        );
      }
    } else {
      const newMap = newItems;
      let setNew = false;
      for (let i = 0; i < combinedList.length; i++) {
        const item = combinedList[i];
        if (isDraggableInterrogatory(item)) {
          setNew = true;
          if (source.index > destination.index) {
            if (i >= destination.index && i < source.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber + 1,
                (newMap.get(item.parentNumber + 1) ?? 0) + 1,
              );
            }
          } else {
            if (i > source.index && i <= destination.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber - 1,
                (newMap.get(item.parentNumber - 1) ?? 0) + 1,
              );
            }
          }
        }
        if (sourceItem.number !== parentNumber) {
          const { orderedList, newIndex } = reorder(
            combinedList,
            source.index,
            destination.index,
          );
          setList(orderedList);
          if (setNew) {
            setNewItems(newMap);
          }
          onEdit(Number.parseInt(draggableId, 10), newIndex);
        } else {
          if (setNew) {
            setNewItems(new Map(newMap));
          }
        }
      }
    }
  };

  let combinedList: (DraggableInterrogatory | Interrogatory)[] =
    getEmptyInterrogatories(0);

  for (const interrogatory of list) {
    combinedList.push(interrogatory);
    combinedList = [
      ...combinedList,
      ...getEmptyInterrogatories(interrogatory.number),
    ];
  }
  return (
    <>
      {combinedList.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="interrogatories-list">
            {(provided) => (
              <div
                className="interrogatories-edit mt-2"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {combinedList.map((interrogatory, index) => (
                  <Draggable
                    key={interrogatory.id}
                    draggableId={`${interrogatory.id}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        className={clsx("selection-item", {
                          isDragging: snapshot.isDragging,
                        })}
                      >
                        {isDraggableInterrogatory(interrogatory) ? (
                          <InterrogatoryAdd
                            onAdd={async (text: string) => {
                              await onAdd(interrogatory.parentNumber, text);
                              removeNewItem(interrogatory.parentNumber, true);
                            }}
                            onDelete={() =>
                              removeNewItem(interrogatory.parentNumber)
                            }
                          />
                        ) : (
                          <InterrogatoryEdit
                            interrogatory={interrogatory}
                            isLastItem={index + 1 === combinedList.length}
                            startingId={
                              interrogatoryId === interrogatory.id.toString()
                            }
                            onAdd={() => {
                              const count =
                                newItems.get(interrogatory.number) || 0;
                              setNewItems(
                                new Map(
                                  newItems.set(interrogatory.number, count + 1),
                                ),
                              );
                            }}
                            onEdit={onEdit}
                            onDelete={() => {
                              setConfirmRemoveId(interrogatory.id);
                            }}
                            documentSubType={documentSubType}
                            startingNumber={startingNumber}
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setNewItems(new Map(newItems.set(0, 1)));
          }}
        >
          <Plus />
          Add new
        </Button>
      )}
      <FormModal
        title={"Are you sure?"}
        cancelText="Cancel"
        confirmText="Yes, remove"
        show={confirmRemoveId != null}
        onClose={() => setConfirmRemoveId(null)}
        onConfirm={handleConfirmDelete}
      >
        <div>
          Are you sure you want to remove this interrogatory? This cannot be
          undone.
        </div>
      </FormModal>
    </>
  );
}

export function InterrogatoryEdit({
  isLastItem,
  interrogatory,
  startingId,
  onAdd,
  onEdit,
  onDelete,
  documentSubType,
  startingNumber,
}: {
  isLastItem: boolean;
  interrogatory: Interrogatory;
  startingId: boolean;
  onAdd: () => void;
  onEdit: (id: number, index: number, text?: string) => void;
  onDelete: () => void;
  documentSubType: DocumentSubType | undefined;
  startingNumber: number;
}) {
  const [content, setContent] = useState(interrogatory.text);
  const [hoverEffect, setHoverEffect] = useState(false);

  const [isHovering, setIsHovering] = useState(false);

  const editingRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch editingRef
  useEffect(() => {
    if (startingId && editingRef.current) {
      setTimeout(() => {
        setHoverEffect(false);
      }, 1000);
      setHoverEffect(true);
      editingRef.current.focus();
    }
  }, [editingRef, startingId]);

  function handleEditingBlur() {
    if (content !== interrogatory.text && content !== "") {
      onEdit(interrogatory.id, interrogatory.number, content);
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setContent(interrogatory.text);
      editingRef.current?.blur();
    } else if (e.key === "Enter") {
      e.preventDefault();
      editingRef.current?.blur();
    }
  }

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  return (
    <div
      className={clsx("interrogatory-item mb-3", {
        selected: hoverEffect,
      })}
      key={interrogatory.id}
      onMouseOver={handleMouseOver}
      onFocus={handleMouseOver}
      onMouseOut={handleMouseOut}
      onBlur={handleMouseOut}
    >
      <div className="main-item">
        <div className="d-flex justify-content-between">
          <div className="interrogatory-title">
            {documentSubType
              ? getDocumentSubTypeRequestName(documentSubType, "")
              : "Question"}{" "}
            {interrogatory.number - 1 + startingNumber}
          </div>
          <div>
            <X className="remove-item" onClick={onDelete} />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <GripVertical className="drag-selection-item" />
          <Form.Control
            as="textarea"
            value={content}
            ref={editingRef}
            style={{ height: "100px" }}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleEditingBlur}
            onKeyDown={handleOnKeyDown}
          />
        </div>
      </div>
      <div
        className={clsx("add-item-wrapper", {
          "hide-item": !isHovering && !isLastItem,
        })}
      >
        <div className="border-line">
          <div className="button-wrapper">
            <Button variant="outline" onClick={onAdd}>
              <Plus />
              Add new
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function InterrogatoryAdd({
  onAdd,
  onDelete,
}: {
  onAdd: (text: string) => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch onAdd
  useEffect(() => {
    // Reset state on rebuild.  Not doing this causes other list items to take over
    // the objects ID.  Doing this outside a useEffect causes an "unmounted" error
    setContent("");
    setIsSaving(false);
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [onAdd]);

  async function handleBlur() {
    if (content !== "") {
      setIsSaving(true);
      onAdd(content);
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
  }

  return (
    <div className="interrogatory-item showHover pb-3" ref={scrollToRef}>
      <div className="main-item">
        <div className="d-flex justify-content-between">
          <div className="interrogatory-title">Adding new item</div>
          <div>
            <X className="remove-item" onClick={onDelete} />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <GripVertical className="drag-selection-item" />
          <Form.Control
            autoFocus
            className="new-item-form"
            as="textarea"
            value={content}
            ref={ref}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleOnKeyDown}
            disabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
}
